import { MMMConfig } from '../types/mmmConfig';
import { apiGet, apiPost, apiDelete, apiUploadFile } from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Service for managing saved datasets and configurations
 */
export const storageService = {
  // ========================================================================
  // Dataset Operations
  // ========================================================================

  /**
   * Save a dataset file
   */
  async saveDataset(
    logto: any,
    file: File,
    name: string,
    description?: string
  ): Promise<{ success: boolean; dataset_id?: string; message: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      if (description) {
        formData.append('description', description);
      }

      return await apiUploadFile(
        `${API_BASE}/api/datasets/save`,
        logto,
        formData
      );
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * List all saved datasets
   */
  async listDatasets(logto: any): Promise<any[]> {
    try {
      const data = await apiGet<{ success: boolean; datasets: any[] }>(
        `${API_BASE}/api/datasets`,
        logto
      );
      return data.datasets || [];
    } catch (error) {
      console.error('Error listing datasets:', error);
      return [];
    }
  },

  /**
   * Get a specific dataset
   */
  async getDataset(
    logto: any,
    datasetId: string
  ): Promise<{ success: boolean; metadata?: any; file_path?: string; message?: string }> {
    try {
      return await apiGet(
        `${API_BASE}/api/datasets/${datasetId}`,
        logto
      );
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Delete a dataset
   */
  async deleteDataset(logto: any, datasetId: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiDelete(
        `${API_BASE}/api/datasets/${datasetId}`,
        logto
      );
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  // ========================================================================
  // Configuration Operations
  // ========================================================================

  /**
   * Save a configuration
   */
  async saveConfig(
    logto: any,
    config: MMMConfig,
    name: string,
    description?: string
  ): Promise<{ success: boolean; config_id?: string; message: string }> {
    try {
      return await apiPost(
        `${API_BASE}/api/configs/save`,
        logto,
        {
          config,
          name,
          description,
        }
      );
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * List all saved configurations
   */
  async listConfigs(logto: any): Promise<any[]> {
    try {
      const data = await apiGet<{ success: boolean; configs: any[] }>(
        `${API_BASE}/api/configs`,
        logto
      );
      return data.configs || [];
    } catch (error) {
      console.error('Error listing configurations:', error);
      return [];
    }
  },

  /**
   * Get a specific configuration
   */
  async getConfig(
    logto: any,
    configId: string
  ): Promise<{ success: boolean; config?: MMMConfig; message?: string }> {
    try {
      return await apiGet(
        `${API_BASE}/api/configs/${configId}`,
        logto
      );
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Delete a configuration
   */
  async deleteConfig(logto: any, configId: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiDelete(
        `${API_BASE}/api/configs/${configId}`,
        logto
      );
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};

